import { browserConsentBridgeSource } from "@moonshot/contracts/browser-consent-bridge";

export const NUXT_CONNECTOR_VERSION = "2.30.3";
export const DEFAULT_CONSENT_POLICY_VERSION = "moonshot-consent-2026-08-06";
export type InstallationTestStatus = {
  status: string;
  checks: { browser: boolean; consent: boolean; edge: boolean };
  consent?: { accepted: boolean; withdrawn: boolean; replayReceived: boolean; purposes: string[]; warning: string | null };
};

export type InstallationGuideInput = {
  origin: string;
  siteId: string;
  publicTagId?: string;
  primaryDomain: string;
  policyVersion: string;
  /** Retained only while old saved setup drafts are migrated. It never changes the installation. */
  stack?: string;
  keyId?: string;
  secret?: string;
};

/** Accept a hostname or homepage URL, without silently discarding credentials,
 * a port, or a page-specific URL. The API independently validates the result.
 */
export function installationHostname(value: string) {
  try {
    const text = value.trim();
    const url = new URL(text.includes('://') ? text : `https://${text}`);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port
      || url.pathname !== '/' || url.search || url.hash || url.hostname.split('.').length < 2) return '';
    return url.hostname.toLowerCase();
  } catch { return ''; }
}

/** Public site identifiers are globally unique, not visitor identifiers. */
export function automaticSiteId(name: string, suffix = crypto.randomUUID().replaceAll('-', '')) {
  const prefix = name.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').slice(0, 30).replace(/^-|-$/g, '') || 'site';
  return `${prefix}-${suffix}`;
}

function cleanOrigin(value: string) {
  const origin = new URL(value);
  // A locally opened dashboard must never hand customers a localhost tag.
  if (["localhost", "127.0.0.1", "[::1]"].includes(origin.hostname)) return "https://moonshot-tracker.web.app";
  if (origin.protocol !== "https:") throw new Error("HTTPS installation origin required");
  return origin.origin;
}

function attribute(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;");
}

function safePublicTagId(input: InstallationGuideInput) {
  const value = (input.publicTagId || input.siteId).trim();
  return /^[a-zA-Z0-9_-]{24,64}$/.test(value) ? value : "PUBLIC_TAG_ID";
}

export function oneTagInstallLine(input: InstallationGuideInput) {
  const origin = cleanOrigin(input.origin);
  return `<script defer referrerpolicy="origin" src="${attribute(`${origin}/embed/${safePublicTagId(input)}.js`)}"></scr${"ipt"}>`;
}

/** Compatibility name for screens that previously showed a separate browser SDK. */
export function browserInstallLine(input: InstallationGuideInput) {
  return oneTagInstallLine(input);
}

export function connectorInstallBlock(guide: ReturnType<typeof createInstallationGuide>) {
  return guide.oneTagLine;
}

export function consentCallbackSnippet(policyVersion: string, language: "de" | "en" = "de", businessForms = false) {
  const instructions = language === "de"
    ? `// Diese Definition erteilt KEINE Zustimmung.\n// moonshotConsentChanged(choice) im echten CMP-Callback aufrufen:\n// nach Auswahl, beim Wiederherstellen einer gültigen Auswahl und bei Widerruf.\n// choice = { status, purposes, cmpSource, expiresAt } aus dem Cookie-Banner.\n// Nur tatsächlich akzeptierte Zwecke übergeben; expiresAt ist der ORIGINAL-Ablauf.\n// visitor_identification + journey: Besucher; session_replay: Video/Heatmap;\n// device_matching: Geräteabgleich.${businessForms ? " business_form_identity: Formularverknüpfung." : ""}\n// Bei Ablehnung/Widerruf: status: "withdrawn", purposes: [].`
    : `// Defining this function does NOT grant consent.\n// Call moonshotConsentChanged(choice) from the actual CMP callback on change,\n// restoration of a valid choice and withdrawal.\n// choice = { status, purposes, cmpSource, expiresAt } from your cookie banner.\n// Pass only accepted purposes and the ORIGINAL expiry; never extend on reload.\n// visitor_identification + journey: visits; session_replay: replay/heatmap;\n// device_matching: device matching.${businessForms ? " business_form_identity: form linking." : ""}\n// On rejection/withdrawal: status: "withdrawn", purposes: [].`;
  // MoonshotConsentQueue in the shared helper also supports CMPs running
  // before the deferred One-Tag has downloaded.
  return `${instructions}\n${browserConsentBridgeSource(policyVersion)}`;
}

export function createInstallationGuide(input: InstallationGuideInput) {
  const oneTagLine = oneTagInstallLine(input);
  return {
    stack: "one_tag" as const,
    installationMode: "one_tag" as const,
    automaticServerInstall: false,
    serverProcessing: "moonshot_managed" as const,
    requiresCustomerServer: false,
    oneTagLine,
    browserLine: oneTagLine,
    serverInstallLine: "",
    environment: "",
    title: "Moonshot One-Tag",
    note: "Ein Tag verbindet die Website mit dem Moonshot-Erkennungsdienst. Kein Serverpaket, API-Schlüssel oder DNS-Eintrag beim Kunden. Voraussetzung: Die Website erlaubt externe Scripts und Verbindungen zu Moonshot."
  };
}

/** Definitions only. Installing this adapter never grants consent by itself. */
export function consentConnectionSnippet(policyVersion: string, format: 'javascript' | 'html', businessForms = false) {
  const source = `${consentCallbackSnippet(policyVersion, 'de', businessForms)}

// Diese Funktion im echten Banner-Callback aufrufen.
// Die Werte müssen aus der gespeicherten, gültigen Banner-Auswahl stammen.
window.updateMoonshotFromBanner = function (selection) {
  const choice = selection || {};
  const allowed = choice.analyticsAllowed === true;
  const purposes = allowed ? ["visitor_identification", "journey"] : [];
  if (allowed && choice.replayAllowed === true) purposes.push("session_replay");
  if (allowed && choice.deviceMatchingAllowed === true) purposes.push("device_matching");${businessForms ? '\n  if (allowed && choice.businessFormsAllowed === true) purposes.push("business_form_identity");' : ''}
  window.moonshotConsentChanged({
    status: allowed ? "accepted" : "withdrawn",
    purposes: purposes,
    cmpSource: choice.cmpSource,
    expiresAt: choice.expiresAt
  });
};`;
  // A policy version must never terminate a customer's inline HTML script.
  return format === 'html' ? `<script>\n${source.replaceAll('<', '\\u003c')}\n</scr${'ipt'}>` : source;
}
